// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { getOnboardingSession } from "../features/onboarding/api/onboardingApi";
import { getWorkspaceSettings } from "../features/onboarding/api/workspaceSettingsApi";
import { getTenantSettings } from "../features/onboarding/api/tenantSettingsApi";
import { resolveWorkspaceOnboarded } from "../features/onboarding/wizardResume";
import type {
  OnboardingSessionModel,
  TeamSize,
  WizardData,
  WorkRole,
} from "../features/onboarding/models/onboardingModels";
import { useAuthSession } from "../hooks/useAuthSession";
import {
  COLD_START_RETRY_BUDGET_MS,
  WAKING_UP_AFTER_MS,
  type WarmUpFailure,
  classifyWarmUpFailure,
  isColdStartRetryable,
  retryColdStart,
} from "../services/api/coldStartRetry";
import { isDevPreviewMode } from "../utils/devEnv";

export const ONBOARDED_STORAGE_KEY = "riddle-onboarded";

export interface WorkspaceProfile {
  workFocus?: WorkRole | undefined;
  teamSizeBand?: TeamSize | undefined;
}

export interface OnboardingContextType {
  isOnboarded: boolean;
  isOnboardingResolved: boolean;
  isOnboardingWakingUp: boolean;
  onboardingReadFailure: WarmUpFailure | undefined;
  retryOnboardingRead: () => void;
  setIsOnboarded: (value: boolean) => void;
  workspaceProfile: WorkspaceProfile;
  refreshWorkspaceProfile: () => Promise<void>;
}

export const OnboardingContext = createContext<
  OnboardingContextType | undefined
>(undefined);

function readStoredOnboarded(): boolean {
  const stored = localStorage.getItem(ONBOARDED_STORAGE_KEY);
  return stored ? JSON.parse(stored) === true : false;
}

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { activeAccount, isAuthenticated, getAccessToken } = useAuthSession();
  const accountKey = activeAccount?.homeAccountId ?? null;
  const [isOnboarded, setIsOnboardedState] = useState<boolean>(() =>
    readStoredOnboarded(),
  );
  const [isOnboardingResolved, setIsOnboardingResolved] = useState<boolean>(
    false,
  );
  const [workspaceProfile, setWorkspaceProfile] = useState<WorkspaceProfile>(
    {},
  );
  const [isOnboardingWakingUp, setIsOnboardingWakingUp] = useState(false);
  const [onboardingReadFailure, setOnboardingReadFailure] = useState<
    WarmUpFailure | undefined
  >(undefined);
  const [readGeneration, setReadGeneration] = useState(0);
  const resolvedRef = useRef<{ done: boolean; identity: string | null }>({
    done: false,
    identity: null,
  });

  const setIsOnboarded = useCallback((value: boolean) => {
    setIsOnboardedState(value);
    setIsOnboardingResolved(true);
    localStorage.setItem(ONBOARDED_STORAGE_KEY, JSON.stringify(value));
  }, []);

  const applyWorkspaceProfile = useCallback(
    async (
      token: string | undefined,
      fallback: WorkspaceProfile | undefined,
    ) => {
      let workspace: WorkspaceProfile | undefined;
      const controller = new AbortController();

      try {
        const tenantSettings = await getTenantSettings({
          ...(token !== undefined ? { token } : {}),
          signal: controller.signal,
        });
        workspace = tenantSettings.settings.workspace;
      } catch {
        workspace = undefined;
      } finally {
        controller.abort();
      }

      workspace ??= fallback;

      setWorkspaceProfile({
        ...(workspace?.workFocus ? { workFocus: workspace.workFocus } : {}),
        ...(workspace?.teamSizeBand
          ? { teamSizeBand: workspace.teamSizeBand }
          : {}),
      });
    },
    [],
  );

  const refreshWorkspaceProfile = useCallback(async () => {
    let token: string | undefined;
    try {
      token = await getAccessToken();
    } catch {
      token = undefined;
    }

    await applyWorkspaceProfile(token, undefined);
  }, [applyWorkspaceProfile, getAccessToken]);

  useEffect(() => {
    const resolved = resolvedRef.current;

    if (resolved.done && resolved.identity === accountKey) {
      return;
    }
    if (!isAuthenticated && !isDevPreviewMode) {
      return;
    }

    if (
      resolved.done &&
      resolved.identity !== null &&
      accountKey !== null &&
      resolved.identity !== accountKey
    ) {
      localStorage.removeItem(ONBOARDED_STORAGE_KEY);
      setIsOnboardedState(false);
      setIsOnboardingResolved(false);
      setWorkspaceProfile({});
    }

    let cancelled = false;
    const controller = new AbortController();
    const wakingTimer = setTimeout(() => setIsOnboardingWakingUp(true), WAKING_UP_AFTER_MS);
    (async () => {
      try {
        const token = await getAccessToken();
        let session: OnboardingSessionModel;
        try {
          session = await retryColdStart(() => getOnboardingSession(token), {
            deadline: Date.now() + COLD_START_RETRY_BUDGET_MS,
            signal: controller.signal,
            isRetryable: isColdStartRetryable,
            canRetry: () => !cancelled,
          });
        } catch (error) {
          if (!cancelled) {
            console.warn("Onboarding session read failed", error);
            setOnboardingReadFailure(classifyWarmUpFailure(error, isColdStartRetryable));
          }
          return;
        }
        let settings: Partial<WizardData> | undefined;
        try {
          settings = await getWorkspaceSettings(token);
        } catch {
          settings = undefined;
        }

        if (cancelled) {
          return;
        }

        void applyWorkspaceProfile(token, settings?.workspace);

        setIsOnboarded(
          resolveWorkspaceOnboarded({
            hasTenantMembership: session.hasTenantMembership,
            settings,
          }),
        );
      } catch {
        if (!cancelled) {
          setIsOnboardingResolved(true);
        }
      } finally {
        clearTimeout(wakingTimer);
        if (!cancelled) {
          setIsOnboardingWakingUp(false);
          resolvedRef.current = { done: true, identity: accountKey };
        }
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(wakingTimer);
      controller.abort();
    };
  }, [
    accountKey,
    isAuthenticated,
    getAccessToken,
    setIsOnboarded,
    applyWorkspaceProfile,
    readGeneration,
  ]);

  const retryOnboardingRead = useCallback(() => {
    resolvedRef.current = { done: false, identity: null };
    setOnboardingReadFailure(undefined);
    setReadGeneration((generation) => generation + 1);
  }, []);

  return (
    <OnboardingContext.Provider
      value={{
        isOnboarded,
        isOnboardingResolved,
        isOnboardingWakingUp,
        onboardingReadFailure,
        retryOnboardingRead,
        setIsOnboarded,
        workspaceProfile,
        refreshWorkspaceProfile,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboardingStatus() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error(
      "useOnboardingStatus must be used within OnboardingProvider",
    );
  }
  return context;
}

export function useWorkspaceProfileRefresh(): () => Promise<void> {
  return useContext(OnboardingContext)?.refreshWorkspaceProfile ?? noopRefresh;
}

async function noopRefresh(): Promise<void> {
  return;
}
